import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react'
import { Segment, Header, Container, Menu, Ref } from 'semantic-ui-react'
import { formatPeriodPrice, getProductPeriodPriceFormat, getServicePeriodPriceFormat, handleRedirect, hasAccessScope } from '../helpers'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './root'
import { useRail } from '../components/dash'
import DataTable from '../components/data_table'
import { getServices } from '../api/autotask/services'
import { getRoles } from '../api/autotask/roles'
import { getProducts } from '../api/autotask/products'

export default function PriceListPage() {
	const routerNavigateFn = useNavigate()
	const { user } = useAuth()
	const [railVisible, setRail] = useRail()
	const userCanViewUnitCost = useMemo(() => hasAccessScope(user, 'service_view_unit_cost'), [user])

	const apiSearchRoles = useCallback(async (query, options, requestOptions) => {
		const res = await getRoles(query, options, requestOptions)
		handleRedirect(res, routerNavigateFn)
		return res
	}, [routerNavigateFn])

	const apiSearchServices = useCallback(async (query, options, requestOptions) => {
		const res = await getServices(query, options, requestOptions)
		handleRedirect(res, routerNavigateFn)
		return res
	}, [routerNavigateFn])

	const apiSearchProducts = useCallback(async (query, options, requestOptions) => {
		const res = await getProducts(query, options, requestOptions)
		handleRedirect(res, routerNavigateFn)
		return res
	}, [routerNavigateFn])

	const columns = useMemo(() => {
		let cols = [
			{ key: 'name', text: 'Navn', visible: true, searchable: true },
			{ key: 'unitPrice', text: 'Veil. utpris/periode', visible: true },
		]
		if (userCanViewUnitCost) {
			cols.splice(1, 0,
				{ key: 'unitCost', text: 'Innpris/periode', visible: true }
			)
		}
		return cols
	}, [userCanViewUnitCost])

	const hourlyRatesRef = useRef(null)
	const servicesRef = useRef(null)
	const productsRef = useRef(null)
	const [activeSegment, setActiveSegment] = useState('hourly-rates')

	useEffect(() => {
		let observer, data = {
			'hourly-rates': { intersectionRatio: 0 },
			'services':     { intersectionRatio: 0 },
			'products':     { intersectionRatio: 0 },
		};

		if (hourlyRatesRef.current && servicesRef.current && productsRef.current) {
			observer = new IntersectionObserver((entries, observer) => {
				entries.forEach(entry => {
					data[entry.target.id].intersectionRatio = entry.intersectionRatio
				})

				// filter candidates by visible elements only
				let candidates = Object.keys(data).filter(key => data[key].intersectionRatio > 0)

				// sort candidates by how visible they are
				candidates.sort((a, b) => data[b].intersectionRatio - data[a].intersectionRatio)

				// set active segment to the most visible candidate
				if (candidates.length > 0) {
					setActiveSegment(candidates[0])
				}
			}, {
				threshold: [0, 0.25, 0.5, 0.75, 1]
			})

			observer.observe(productsRef.current)
			observer.observe(servicesRef.current)
			observer.observe(hourlyRatesRef.current)
		}
		return () => {
			if (observer) observer.disconnect()
		}
	}, [hourlyRatesRef, servicesRef, productsRef])

	const navigationMenu = useMemo(() => <>
		<Menu vertical secondary tabular={railVisible ? 'right' : null}>
			<Menu.Item
				name="hourly-rates"
				href="#hourly-rates"
				active={activeSegment === 'hourly-rates'}
				content="Timepriser"
				as="a"
				onClick={e => {
					if (hourlyRatesRef.current && !e.defaultPrevented) {
						e.preventDefault()
						hourlyRatesRef.current.scrollIntoView({ behavior: 'smooth' })
					}
				}}
			/>
			<Menu.Item
				name="services"
				href="#services"
				active={activeSegment === 'services'}
				content="Tjenester"
				as="a"
				onClick={e => {
					if (servicesRef.current && !e.defaultPrevented) {
						e.preventDefault()
						servicesRef.current.scrollIntoView({ behavior: 'smooth' })
					}
				}}
			/>
			<Menu.Item
				name="products"
				href="#products"
				active={activeSegment === 'products'}
				content="Produkter"
				as="a"
				onClick={e => {
					if (productsRef.current && !e.defaultPrevented) {
						e.preventDefault()
						productsRef.current.scrollIntoView({ behavior: 'smooth' })
					}
				}}
			/>
		</Menu>
	</>, [railVisible, activeSegment])

	useEffect(() => {
		setRail(navigationMenu)
		return () => setRail(null)
	}, [navigationMenu, setRail])

	return <>
		<Segment vertical padded="very">
			<Container>
				<Header size="huge">
					Prisliste
					<Header.Subheader>Liste over veiledende priser for tjenester fra Konsept-IT</Header.Subheader>
				</Header>

				{railVisible ? null : navigationMenu}
			</Container>
		</Segment>

		<Ref innerRef={hourlyRatesRef}>
			<Segment vertical padded="very" id="hourly-rates">
				<Container>
					<Header size="large">Timepriser</Header>

					<DataTable
						format="autotask"
						columns={[
							{ key: 'name', text: 'Navn', visible: true, searchable: true },
							{ key: 'hourlyRate', text: 'Veil. utpris/periode', visible: true },
						]}
						apiSearch={apiSearchRoles}
						query={[{'op': 'eq', 'field': 'isActive', 'value': true}]}
						renderRow={data => [
							data.name,
							formatPeriodPrice(data.hourlyRate, '%s kr/time'),
						]}
					/>
				</Container>
			</Segment>
		</Ref>

		<Ref innerRef={servicesRef}>
			<Segment vertical padded="very" id="services">
				<Container>
					<Header size="large">Tjenester</Header>

					<DataTable
						format="autotask"
						columns={columns}
						apiSearch={apiSearchServices}
						query={[{'op': 'eq', 'field': 'isActive', 'value': true}]}
						renderRow={data => {
							let cols = [
								data.name,
								formatPeriodPrice(data.unitPrice, getServicePeriodPriceFormat(data.periodType.value)),
							]
							if (userCanViewUnitCost) {
								cols.splice(1, 0,
									formatPeriodPrice(data.unitCost, getServicePeriodPriceFormat(data.periodType.value)),
								)
							}
							return cols
						}}
					/>
				</Container>
			</Segment>
		</Ref>

		<Ref innerRef={productsRef}>
			<Segment vertical padded="very" id="products">
				<Container>
					<Header size="large">Produkter</Header>

					<DataTable
						format="autotask"
						columns={columns}
						apiSearch={apiSearchProducts}
						query={[{'op': 'eq', 'field': 'isActive', 'value': true}, {'op': 'exist', 'field': 'periodType'}]}
						renderRow={data => {
							let cols = [
								data.name,
								formatPeriodPrice(data.unitPrice, getProductPeriodPriceFormat(data.periodType.value)),
							]
							if (userCanViewUnitCost) {
								cols.splice(1, 0,
									formatPeriodPrice(data.unitCost, getProductPeriodPriceFormat(data.periodType.value)),
								)
							}
							return cols
						}}
					/>
				</Container>
			</Segment>
		</Ref>
	</>
}